import React from "react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { AlertTriangle } from "lucide-react";
import ForecastLayout from "../../components/forecast/ForecastLayout";
import { KpiStrip, DriverPanel, LineButton, PageState } from "../../components/forecast/Lines";
import { Tile } from "../../components/Primitives";
import { useChartColors } from "../../lib/chartColors";
import { usePage, peso } from "../../lib/forecastApi";
import { days } from "../../lib/workcapApi";
import { cn } from "../../lib/utils";

const tick = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function Projection({ rows }) {
  return (
    <Tile className="lg:col-span-7" dense testid="fc-cash-list" title={`Projection by branch × category · top ${rows.length}`}>
      <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-surface z-10"><tr className="text-[10px] uppercase tracking-widest text-muted">
            {["Branch · category", "DIO now", "30 d", "60 d", "90 d", "CCC 90 d", "Flag"].map((h, i) => <th key={h} className={cn("py-2 px-3 border-b border-border whitespace-nowrap", i ? "text-right" : "text-left")}>{h}</th>)}</tr></thead>
          <tbody className="font-mono-tabular">{rows.map(r => (
            <LineButton key={r.line} as="tr" line={{ id: r.line }} className="border-b border-border/60 table-row">
              <td className="py-2 px-3"><div className="font-sans text-[12.5px] whitespace-nowrap">{r.category}</div><div className="text-[10px] text-muted whitespace-nowrap">{r.branch_name}</div></td>
              <td className="py-2 px-3 text-right whitespace-nowrap">{days(r.dio_now)}</td><td className="py-2 px-3 text-right whitespace-nowrap">{days(r.dio_30d)}</td>
              <td className="py-2 px-3 text-right whitespace-nowrap">{days(r.dio_60d)}</td><td className="py-2 px-3 text-right whitespace-nowrap">{days(r.dio_90d)}</td>
              <td className="py-2 px-3 text-right whitespace-nowrap">{days(r.ccc_90d)}</td>
              <td className="py-2 px-3 text-right whitespace-nowrap">{r.cash_crunch_in_days ? <span className="text-critical">Crunch in {r.cash_crunch_in_days} d</span> : <span className="text-muted">—</span>}</td>
            </LineButton>))}</tbody>
        </table>
      </div>
    </Tile>
  );
}

function Fixes({ fixes, crunch }) {
  return (
    <Tile testid="fc-cash-fixes" title="Suggested fixes · cash freed">
      <div className="text-[11.5px] text-muted mb-2" data-testid="fc-cash-crunch-note">
        {crunch.length ? <span className="text-critical inline-flex gap-1"><AlertTriangle size={12} className="mt-0.5" />Cash crunch flagged: {crunch.slice(0, 3).map(c => `${c.category} @ ${c.branch_name} in ${c.cash_crunch_in_days} d`).join(" · ")}</span>
          : "No cash crunch in the next 90 days — fixes below target the lines with the highest DIO."} Suggestions only: nothing is ordered, cancelled or priced automatically.</div>
      <div className="divide-y divide-border max-h-[360px] overflow-y-auto">
        {fixes.map((f, i) => (
          <div key={i} className="py-2 flex gap-3 text-[12px]" data-testid={`fc-cash-fix-${i}`}>
            <div className="flex-1 min-w-0"><div className="font-medium">{f.fix}</div><div className="text-muted truncate">{f.sku_name} · {f.branch_name} · {f.detail}</div></div>
            <div className="font-mono-tabular text-success shrink-0">{peso(f.cash_freed_php || 0)}</div>
          </div>))}
        {!fixes.length && <div className="py-3 text-muted text-[12px]">No fixes suggested in the latest engine cycle.</div>}
      </div>
    </Tile>
  );
}

// Cash & Working Capital Forecast: DIO / CCC / GMROI 30-60-90 days ahead, cash-crunch flags and engine-backed fixes.
export default function CashPage() {
  const { data, error, forbidden, reload } = usePage("cash");
  const C = useChartColors();
  return (
    <ForecastLayout title="Cash & Working Capital Forecast"
      description="DIO, CCC and GMROI today and 30, 60 and 90 days ahead — from the demand forecast, NASA POWER heat, open POs, stock on hand, receivable and payable days and optional building permits.">
      {!data ? <PageState error={error} forbidden={forbidden} onRetry={reload} /> : (
        <>
          <KpiStrip kpis={data.kpis} />
          {data.scope && <div className="text-[11.5px] text-muted -mt-2" data-testid="fc-cash-scope">{data.scope}</div>}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Projection rows={data.rows} />
            <div className="lg:col-span-5 space-y-6">
              <Tile testid="fc-cash-chart" title="Network DIO and CCC · today to 90 days">
                <div style={{ height: 220 }}><ResponsiveContainer>
                  <BarChart data={data.chart} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
                    <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="horizon" tick={tick} stroke={C.borderStrong} /><YAxis tick={tick} stroke={C.borderStrong} />
                    <Tooltip contentStyle={{ background: C.surface, border: `1px solid ${C.borderStrong}`, fontSize: 11 }} /><Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="dio" name="DIO (days)" fill={C.chart[0]} isAnimationActive={false} />
                    <Bar dataKey="ccc" name="CCC (days)" fill={C.chart[2]} isAnimationActive={false} />
                  </BarChart></ResponsiveContainer></div>
              </Tile>
              <Fixes fixes={data.fixes} crunch={data.crunch} />
              <DriverPanel testid="fc-cash-drivers" title="Drivers" hint="Click a driver for its explanation and source rows" drivers={data.drivers} centered />
            </div>
          </div>
        </>)}
    </ForecastLayout>
  );
}
